import { sign } from "crypto";
import { DeadEndType, TrackTin, SignalType, RailwayZones } from "../models/model";


export enum TagTypeCode {
  S = 'S',
  N = 'N',
  G = 'G',
  L = 'L',
  A = 'A',
  X = 'X',
  T = 'T'
}

export enum RedAndGreenColor {
  RED = 'red',
  GREEN = 'green',
}

export const RED_AND_GREEN_APPLICABLE_ZONES: RailwayZones[] = [
  RailwayZones.WESTERN_RAILWAY,
  RailwayZones.CENTRAL_RAILWAY,
];

export enum RailwayZoneShortCodes {
  'Central Railway' = 'C',
  'Eastern Railway' = 'E',
  'Northern Railway' = 'N',
  'North Eastern Railway' = 'N.E',
  'Northeast Frontier Railway' = 'N.F',
  'North Western Railway' = 'N.W',
  'Southern Railway' = 'S',
  'South Central Railway' = 'S.C',
  'South Eastern Railway' = 'S.E',
  'South Western Railway' = 'S.W',
  'Western Railway' = 'W',
  'West Central Railway' = 'W.C',
  'East Central Railway' = 'E.C',
  'East Coast Railway' = 'E.Co',
  'Northern Central Railway' = 'N.C',
  'North Central Railway' = 'N.C',
  'South East Central Railway' = 'S.E.C',
  'South Coast Railway' = 'S.Co'
}
// ── Raised A-Symbol & AG-Marker dimension constants ──

export const RAISED_A_SYMBOL = {
  /** Vertical length when A symbol points down / up (automatic signals) */
  AUTO_VERTICAL_DOWN: 23,
  AUTO_VERTICAL_UP: -16,
  /** Vertical length when A symbol points down / up (semi-auto signals) */
  SEMI_AUTO_VERTICAL_DOWN: 24,
  SEMI_AUTO_VERTICAL_DOWN_SECR_LEFT: 40,
  SEMI_AUTO_VERTICAL_UP: -24,
  /** Horizontal length (semi-auto, non-NCR) */
  SEMI_AUTO_HORIZONTAL_DOWN: 15,
  SEMI_AUTO_HORIZONTAL_UP: 25,
  /** Image size */
  IMAGE_SIZE: 15,
};

export const AG_MARKER = {
  /** Vertical length when AG marker points down / up */
  VERTICAL_DOWN: 35,
  VERTICAL_UP: -40,
  /** Horizontal length (semi-auto, non-NCR) */
  HORIZONTAL_LEFT: -25,
  HORIZONTAL_RIGHT: 25,
  /** Image size */
  IMAGE_SIZE: 15,
  IMAGE_SIZE_SEMI_AUTO_NON_NCR: 14,
};

export const CIRCUIT_TYPES = {
  TRACK_POINTS: 'Track Points',
  APPROACHING_STOP_BOUNDARY: 'Approaching Stop Boundary',
  TRACK_CIRCUITS: 'Track Circuits',
  AXLE_COUNTERS: 'Axle Counters'
};

export const AXLE_COUNTERS_TYPE = CIRCUIT_TYPES.AXLE_COUNTERS;

/** Sub-type names for TrackCircuitsComponent.getCircuitSvg, matched case-insensitively. */
export const TRACK_CIRCUIT_SUB_TYPES = {
  IL_BOTTOM_RIGHT: 'IL Bottom Right',
  IL_TOP_LEFT: 'IL Top Left',
  IL_TOP_RIGHT: 'IL Top Right',
  IL_BOTTOM_LEFT: 'IL Bottom Left',
  L_BOTTOM_RIGHT: 'L Bottom Right',
  L_TOP_LEFT: 'L Top Left',
  L_TOP_RIGHT: 'L Top Right',
  L_BOTTOM_LEFT: 'L Bottom Left',
  STRICT_RIGHT: 'Strict Right',
  STRICT_LEFT: 'Strict Left',
  MANUAL_TRAP_POINT: 'Manual Trap Point',
} as const;

/** Sub-type names for AxleCountersComponent.getAxleCounterSvg, matched case-insensitively. */
export const AXLE_COUNTER_SUB_TYPES = {
  SINGLE_AXLE: 'Single Axle',
  DOUBLE_AXLE: 'Double Axle',
  DC_AXLE: 'DC Axle',
  DOUBLE_AXLE_HORIZONTAL: 'Double Axle Horizontal',
  DOUBLE_AXLE_VERTICAL: 'Double Axle Vertical',
  DOUBLE_AXLE_HORIZONTAL_LEFT: 'Double Axle Horizontal Left',
  CR_SINGLE_AXLE: 'CR Single Axle',
  CR_LEFT_SLANT_SINGLE_AXLE: 'CR Left Slant Single Axle',
  CR_DOUBLE_AXLE: 'CR Double Axle',
  CR_DOUBLE_AXLE_V2: 'CR Double Axle V2',
  CR_DOUBLE_AXLE_V3: 'CR Double Axle V3',
  DOT_AXLE: 'Dot Axle',
  VERTICAL_SINGLE_AXLE: 'Vertical Single Axle',
  DOUBLE_AXLE_VERTICAL_V2: 'Double Axle Vertical V2',
  TRACK_CIRCUIT_SINGLE: 'Track Circuit Single',
  TRACK_CIRCUIT_DOUBLE: 'Track Circuit Double',
  SINGLE_AXLE_LEFT: 'Single Axle Left',
  DOUBLE_AXLE_COUNTER_STRAIGHT: 'Double Axle Counter Straight',
  CR_SINGLE_AXLE_UNFILLED: 'CR Single Axle Unfilled',
} as const;

/** Sub-type names for TrackPointsComponent.getTrackPointsSvg, matched case-insensitively. */
export const TRACK_POINT_SUB_TYPES = {
  SINGLE_TRACK_POINT: 'Single Track Point',
  LEFT_SLANT_SINGLE_TRACK_POINT: 'Left Slant Single Track Point',
  DOUBLE_TRACK_POINT: 'Double Track Point',
} as const;

/** Old/new symbol gap for a dead end type change, keyed by type. Falls back to 80 for wider symbols. */
export const DEAD_END_TYPE_CHANGE_SPACING: Record<string, number> = {
  [DeadEndType.BS_DEAD_END_NON_KAVACH]: 40,
  [DeadEndType.BS_DEAD_END_KAVACH]: 40,
  [DeadEndType.LONG_DEAD_END]: 60,
  [DeadEndType.END_KNOB]: 60
};

export const sectionRangeColors: {
    "Station Section": string;
    "Block Section": string;
    "Berthing Section": string;
    [key: string]: string;
  } = {
    "Station Section": '#FF6B6B',    // Red
    "Block Section": '#4ECDC4',      // Blue/Teal  
    "Berthing Section": '#FFE66D'    // Yellow
  };

export const colors = {
    light_grey: '#CCCCCC'
}
export interface AuthorReviewPerson {
  role: string;
  name: string;
  designation?: string;
  signature?: string;
}

export const AUTHOR_REVIEW_PEOPLE: AuthorReviewPerson[] = [
  {
    role: 'prepared by',
    designation: 'DESIGN ENGINEER',
    name: 'M.KRISHNA VAMSI',
    signature: './assets/imgs/signatures/krishna_vamsi.png'
  },
  {
    role: 'checked by',
    name: 'S.GOWTHAM VARMA',
    designation: 'V&V ENGINEER',
    signature: './assets/imgs/signatures/sg_varma.png'
  },
  {
    role: 'verified by',
    name: 'S.JITENDRA VIJAY',
    designation: 'DESIGN DIRECTOR',
    signature: './assets/imgs/signatures/s_jitendra.png'
  },
  {
    role: 'verified by',
    name: 'S.GANGOPADHYAY',
    designation: 'PROOF CONSULTANT'
  }
];

export const WR_AUTHOR_REVIEW_PEOPLE: AuthorReviewPerson[] = [
  {
    role: 'Prepared By',
    designation: 'DESIGN ENGINEER',
    name: 'S SREEJA',
    signature: './assets/imgs/signatures/sreeja.png'
  },
    {
    role: 'Checked By',
    name: 'T.V.L.N.P.PHANI KUMAR',
    designation: 'DESIGN DIRECTOR',
    signature: './assets/imgs/signatures/phani.png'
  },
   {
    role: 'Checked By',
    name: 'S.GOWTHAM VARMA',
    designation: 'V&V ENGINEER',
    signature: './assets/imgs/signatures/sg_varma.png'
  },
  {
    role: 'Verified By',
    name: 'S.JITENDRA VIJAY',
    designation: 'Sr. MANAGER V&V',
    signature: './assets/imgs/signatures/s_jitendra.png'
  },
  {
    role: 'Verified By',
    name: 'MANMOHAN SHARMA',
    designation: 'PROOF CONSULTANT'
  }

];

export const WR_TAG_DATA_REVIEW_PEOPLE: AuthorReviewPerson[] = [
  {
    role: 'prepared by',
    designation: 'DESIGN ENGINEER',
    name: 'B SAI',
    signature: './assets/imgs/signatures/sai.png'
  },
   {
    role: 'checked by',
    name: 'S.SUMANTH REDDY',
    designation: 'V&V ENGINEER',
    signature: './assets/imgs/signatures/s_reddy.png'
  },
  {
    role: 'verified by',
    name: 'S.JITENDRA VIJAY',
    designation: 'Sr. MANAGER V&V',
    signature: './assets/imgs/signatures/s_jitendra.png'
  },
  {
    role: 'verified by',
    name: 'MANMOHAN SHARMA',
    designation: 'PROOF CONSULTANT'
  }

];

export const WCR_TAG_DATA_REVIEW_PEOPLE: AuthorReviewPerson[] = [
  {
    role: 'prepared by',
    designation: 'DESIGN ENGINEER',
    name: 'CH MAHESH',
    signature: './assets/imgs/signatures/mahesh.png'
  },
   {
    role: 'checked by',
    name: 'S.GOWTHAM VARMA',
    designation: 'V&V ENGINEER',
    signature: './assets/imgs/signatures/sg_varma.png'
  },
  {
    role: 'verified by',
    name: 'S.JITENDRA VIJAY',
    designation: 'DESIGN DIRECTOR',
    signature: './assets/imgs/signatures/s_jitendra.png'
  },
  {
    role: 'verified by',
    name: 'S.GANGOPADHYAY',
    designation: 'PROOF CONSULTANT'
  }

];




export const sectionColors = {
    berthing_section: '#FFE66D',
    block_section: '#4ECDC4',
    station_section: '#FF6B6B'
}



export const track_section_points = {
    "Approaching Stop Boundary": [
      {name: "Approach Up Left", type: 'Approaching Stop Boundary', sub_type: 'Approach Up Left', id: "approaching_stop_boundary_up_left", imgPath: "./../../assets/imgs/signal_aspect_images/approaching_station_boundary_western.png"},
      {name: "Approach Up Right", type: 'Approaching Stop Boundary', sub_type: 'Approach Up Right', id: "approaching_stop_boundary_up_right", imgPath: "./../../assets/imgs/signal_aspect_images/approaching_station_boundary_western.png"},
      {name: "Approach Down Left", type: 'Approaching Stop Boundary', sub_type: 'Approach Down Left', id: "approaching_stop_boundary_down_left", imgPath: "./../../assets/imgs/signal_aspect_images/approaching_station_boundary_western.png"},
      {name: "Approach Down Right", type: 'Approaching Stop Boundary', sub_type: 'Approach Down Right', id: "approaching_stop_boundary_down_right", imgPath: "./../../assets/imgs/signal_aspect_images/approaching_station_boundary_western.png"},
      {name: "Approach Up Left Dotted", type: 'Approaching Stop Boundary', sub_type: 'Approach Up Left Dotted', id: "approaching_stop_boundary_up_left_dotted", imgPath: "./../../assets/imgs/signal_aspect_images/Approching_Stop_Signal.svg"},
      {name: "Approach Up Right Dotted", type: 'Approaching Stop Boundary', sub_type: 'Approach Up Right Dotted', id: "approaching_stop_boundary_up_right_dotted", imgPath: "./../../assets/imgs/signal_aspect_images/Approching_Stop_Signal.svg"},
      {name: "Approach Down Left Dotted", type: 'Approaching Stop Boundary', sub_type: 'Approach Down Left Dotted', id: "approaching_stop_boundary_down_left_dotted", imgPath: "./../../assets/imgs/signal_aspect_images/Approching_Stop_Signal.svg"},
      {name: "Approach Down Right Dotted", type: 'Approaching Stop Boundary', sub_type: 'Approach Down Right Dotted', id: "approaching_stop_boundary_down_right_dotted", imgPath: "./../../assets/imgs/signal_aspect_images/Approching_Stop_Signal.svg"},

      {name: "Approach Big Circle Up Left", type: 'Approaching Stop Boundary', sub_type: 'Approach Big Circle Up Left', id: "approaching_stop_big_circle_up_left", imgPath: "./../../assets/imgs/signal_aspect_images/approaching_station_boundary_western.png"},
      {name: "Approach Big Circle Up Right", type: 'Approaching Stop Boundary', sub_type: 'Approach Big Circle Up Right', id: "approaching_stop_big_circle_up_right", imgPath: "./../../assets/imgs/signal_aspect_images/approaching_station_boundary_western.png"},
      {name: "Approach Big Circle Down Left", type: 'Approaching Stop Boundary', sub_type: 'Approach Big Circle Down Left', id: "approaching_stop_big_circle_down_left", imgPath: "./../../assets/imgs/signal_aspect_images/approaching_station_boundary_western.png"},
      {name: "Approach Big Circle Down Right", type: 'Approaching Stop Boundary', sub_type: 'Approach Big Circle Down Right', id: "approaching_stop_big_circle_down_right", imgPath: "./../../assets/imgs/signal_aspect_images/approaching_station_boundary_western.png"},
      {name: "Approach Big Circle Up Left Dotted", type: 'Approaching Stop Boundary', sub_type: 'Approach Big Circle Up Left Dotted', id: "approaching_stop_big_circle_up_left_dotted", imgPath: "./../../assets/imgs/signal_aspect_images/Approching_Stop_Signal.svg"},
      {name: "Approach Big Circle Up Right Dotted", type: 'Approaching Stop Boundary', sub_type: 'Approach Big Circle Up Right Dotted', id: "approaching_stop_big_circle_up_right_dotted", imgPath: "./../../assets/imgs/signal_aspect_images/Approching_Stop_Signal.svg"},
      {name: "Approach Big Circle Down Left Dotted", type: 'Approaching Stop Boundary', sub_type: 'Approach Big Circle Down Left Dotted', id: "approaching_stop_big_circle_down_left_dotted", imgPath: "./../../assets/imgs/signal_aspect_images/Approching_Stop_Signal.svg"},
      {name: "Approach Big Circle Down Right Dotted", type: 'Approaching Stop Boundary', sub_type: 'Approach Big Circle Down Right Dotted', id: "approaching_stop_big_circle_down_right_dotted", imgPath: "./../../assets/imgs/signal_aspect_images/Approching_Stop_Signal.svg"},

      { name: "BLB Up Left", type: 'Approaching Stop Boundary', sub_type: 'BLB Up Left', id: "blb_up_left", imgPath: "./../../assets/imgs/signal_aspect_images/blb_signal.svg" },
      { name: "BLB Up Right", type: 'Approaching Stop Boundary', sub_type: 'BLB Up Right', id: "blb_up_right", imgPath: "./../../assets/imgs/signal_aspect_images/blb_signal_right.svg" },
      { name: "BLB Down Left", type: 'Approaching Stop Boundary', sub_type: 'BLB Down Left', id: "blb_down_left", imgPath: "./../../assets/imgs/signal_aspect_images/blb_signal.svg" },
      { name: "BLB Down Right", type: 'Approaching Stop Boundary', sub_type: 'BLB Down Right', id: "blb_down_right", imgPath: "./../../assets/imgs/signal_aspect_images/blb_signal_right.svg" },
      { name: "BLB UP Left Dotted", type: 'Approaching Stop Boundary', sub_type: 'BLB Up Left Dotted', id: "blb_up_left_dotted", imgPath: "./../../assets/imgs/signal_aspect_images/blb_signal_dotted.svg" },
      { name: "BLB Up Right Dotted", type: 'Approaching Stop Boundary', sub_type: 'BLB Up Right Dotted', id: "blb_up_right_dotted", imgPath: "./../../assets/imgs/signal_aspect_images/blb_signal_right_dotted.svg" },
      { name: "BLB Down Left Dotted", type: 'Approaching Stop Boundary', sub_type: 'BLB Down Left Dotted', id: "blb_down_left_dotted", imgPath: "./../../assets/imgs/signal_aspect_images/blb_signal_dotted.svg" },
      { name: "BLB Down Right Dotted", type: 'Approaching Stop Boundary', sub_type: 'BLB Down Right Dotted', id: "blb_down_right_dotted", imgPath: "./../../assets/imgs/signal_aspect_images/blb_signal_right_dotted.svg" },
      { name:"BLB Circle Up Left", type: 'Approaching Stop Boundary', sub_type: 'BLB Circle Up Left', id: "blb_circle_up_left", imgPath: "./../../assets/imgs/signal_aspect_images/blb_signal_circle.svg" },
      { name:"BLB Circle Up Right", type: 'Approaching Stop Boundary', sub_type: 'BLB Circle Up Right', id: "blb_circle_up_right", imgPath: "./../../assets/imgs/signal_aspect_images/blb_signal_circle_right.svg" },
      { name:"BLB Circle Down Left", type: 'Approaching Stop Boundary', sub_type: 'BLB Circle Down Left', id: "blb_circle_down_left", imgPath: "./../../assets/imgs/signal_aspect_images/blb_signal_circle.svg" },
      { name:"BLB Circle Down Right", type: 'Approaching Stop Boundary', sub_type: 'BLB Circle Down Right', id: "blb_circle_down_right", imgPath: "./../../assets/imgs/signal_aspect_images/blb_signal_circle_right.svg" },
      { name:"BLB Circle Up Left Dotted", type: 'Approaching Stop Boundary', sub_type: 'BLB Circle Up Left Dotted', id: "blb_circle_up_left_dotted", imgPath: "./../../assets/imgs/signal_aspect_images/blb_signal_circle_dotted.svg" },
      { name:"BLB Circle Up Right Dotted", type: 'Approaching Stop Boundary', sub_type: 'BLB Circle Up Right Dotted', id: "blb_circle_up_right_dotted", imgPath: "./../../assets/imgs/signal_aspect_images/blb_signal_circle_right_dotted.svg" },
      { name:"BLB Circle Down Left Dotted", type: 'Approaching Stop Boundary', sub_type: 'BLB Circle Down Left Dotted', id: "blb_circle_down_left_dotted", imgPath: "./../../assets/imgs/signal_aspect_images/blb_signal_circle_dotted.svg" },
      { name:"BLB Circle Down Right Dotted", type: 'Approaching Stop Boundary', sub_type: 'BLB Circle Down Right Dotted', id: "blb_circle_down_right_dotted", imgPath: "./../../assets/imgs/signal_aspect_images/blb_signal_circle_right_dotted.svg" },
    ],
    "Track Points": [
      {name: "Single Track Point", type: CIRCUIT_TYPES.TRACK_POINTS, sub_type: TRACK_POINT_SUB_TYPES.SINGLE_TRACK_POINT, id: "single_track", imgPath: "./../../assets/imgs/circuit_images/svgs/single_track.svg"},
      {name: "Left Slant Single Track Point", type: CIRCUIT_TYPES.TRACK_POINTS, sub_type: TRACK_POINT_SUB_TYPES.LEFT_SLANT_SINGLE_TRACK_POINT, id: "left_slant_single_track", imgPath: "./../../assets/imgs/circuit_images/svgs/left_slant_single_track.svg"},
      {name: "Double Track Point", type: CIRCUIT_TYPES.TRACK_POINTS, sub_type: TRACK_POINT_SUB_TYPES.DOUBLE_TRACK_POINT, id: "double_track", imgPath: "./../../assets/imgs/circuit_images/svgs/double_track.svg"},
    ],
    "Axle Counters": [
      {name: "Single Axle", type: CIRCUIT_TYPES.AXLE_COUNTERS, sub_type: AXLE_COUNTER_SUB_TYPES.SINGLE_AXLE,  id: "single_axle_counter", imgPath: "./../../assets/imgs/circuit_images/svgs/single_axle_counter_v2.svg"},
      {name: "Single Axle Left", type: CIRCUIT_TYPES.AXLE_COUNTERS, sub_type: AXLE_COUNTER_SUB_TYPES.SINGLE_AXLE_LEFT,  id: "single_axle_counter_left", imgPath: "./../../assets/imgs/circuit_images/svgs/single_axle_counter_v3.svg"},
      {name: "Vertical Single Axle", type: CIRCUIT_TYPES.AXLE_COUNTERS, sub_type: AXLE_COUNTER_SUB_TYPES.VERTICAL_SINGLE_AXLE,  id: "vertical_single_axle_counter", imgPath: "./../../assets/imgs/circuit_images/svgs/single_axle_counter_vertical.svg"},
      {name: "Double Axle", type: CIRCUIT_TYPES.AXLE_COUNTERS, sub_type: AXLE_COUNTER_SUB_TYPES.DOUBLE_AXLE, id: "double_axle_counter", imgPath: "./../../assets/imgs/circuit_images/svgs/double_axle_counter_v2.svg"},
      {name: "DC Axle", type: CIRCUIT_TYPES.AXLE_COUNTERS, sub_type: AXLE_COUNTER_SUB_TYPES.DC_AXLE, id: "dc_axle_counter", imgPath: "./../../assets/imgs/circuit_images/svgs/dc_axle_counter.svg"},
      {name: "Horizontal Double Axle", type: CIRCUIT_TYPES.AXLE_COUNTERS, sub_type: AXLE_COUNTER_SUB_TYPES.DOUBLE_AXLE_HORIZONTAL, id: "dc_axle_counter_horizontal", imgPath: "./../../assets/imgs/circuit_images/svgs/double_axle_counter.svg"},
      {name: "Vertical Double Axle", type: CIRCUIT_TYPES.AXLE_COUNTERS, sub_type: AXLE_COUNTER_SUB_TYPES.DOUBLE_AXLE_VERTICAL, id: "dc_axle_counter_vertical", imgPath: "./../../assets/imgs/circuit_images/svgs/double_axle_counter_vertcial.svg"},
      {name: "Vertical Double Axle v2", type: CIRCUIT_TYPES.AXLE_COUNTERS, sub_type: AXLE_COUNTER_SUB_TYPES.DOUBLE_AXLE_VERTICAL_V2, id: "dc_axle_counter_vertical_v2", imgPath: "./../../assets/imgs/circuit_images/svgs/vertical_double_axle_v2.svg"},
      {name: "Horizontal Double Axle Left", type: CIRCUIT_TYPES.AXLE_COUNTERS, sub_type: AXLE_COUNTER_SUB_TYPES.DOUBLE_AXLE_HORIZONTAL_LEFT, id: "dc_axle_counter_horizontal_left", imgPath: "./../../assets/imgs/circuit_images/svgs/double_axle_counter_left_slant.svg"},
      {name: "Double Axle Counter Straight", type: CIRCUIT_TYPES.AXLE_COUNTERS, sub_type: AXLE_COUNTER_SUB_TYPES.DOUBLE_AXLE_COUNTER_STRAIGHT, id:"double_axle_counter_straight", imgPath:"./../../assets/imgs/circuit_images/svgs/double_axle_counter_straight.svg"},
      {name: "CR Single Axle", type: CIRCUIT_TYPES.AXLE_COUNTERS, sub_type: AXLE_COUNTER_SUB_TYPES.CR_SINGLE_AXLE, id: "cr_single_axle_counter", imgPath: "./../../assets/imgs/circuit_images/svgs/single_axle_counter_central.svg"},
      {name: "CR Left Slant Single Axle", type: CIRCUIT_TYPES.AXLE_COUNTERS, sub_type: AXLE_COUNTER_SUB_TYPES.CR_LEFT_SLANT_SINGLE_AXLE, id: "cr_left_slant_single_axle_counter", imgPath: "./../../assets/imgs/circuit_images/svgs/left_slant_single_axle_counter.svg"},
      {name: "CR double Axle", type: CIRCUIT_TYPES.AXLE_COUNTERS, sub_type: AXLE_COUNTER_SUB_TYPES.CR_DOUBLE_AXLE, id: "cr_double_axle_counter", imgPath: "./../../assets/imgs/circuit_images/svgs/double_axle_counter_central.svg"},
      {name: "CR double Axle v2", type: CIRCUIT_TYPES.AXLE_COUNTERS, sub_type: AXLE_COUNTER_SUB_TYPES.CR_DOUBLE_AXLE_V2, id: "cr_double_axle_counter_v2", imgPath: "./../../assets/imgs/circuit_images/svgs/double_axle_counter_v3.svg"},
      { name: "Dot Axle", type: CIRCUIT_TYPES.AXLE_COUNTERS, sub_type: AXLE_COUNTER_SUB_TYPES.DOT_AXLE, id: "dot_axle_counter", imgPath: "./../../assets/imgs/circuit_images/svgs/dot_axle.svg" },
      { name: "Tc Single ",type: CIRCUIT_TYPES.AXLE_COUNTERS,sub_type: AXLE_COUNTER_SUB_TYPES.TRACK_CIRCUIT_SINGLE ,id: "track_circuit_single" ,imgPath: "./../../assets/imgs/circuit_images/Track_Circuit.svg"},
      { name: "Tc Double ",type: CIRCUIT_TYPES.AXLE_COUNTERS,sub_type: AXLE_COUNTER_SUB_TYPES.TRACK_CIRCUIT_DOUBLE,id: "track_circuit_double",imgPath: "./../../assets/imgs/circuit_images/Track_Circuit2.svg"},
      {name: "CR Single Axle Unfilled", type: CIRCUIT_TYPES.AXLE_COUNTERS, sub_type: AXLE_COUNTER_SUB_TYPES.CR_SINGLE_AXLE_UNFILLED, id: "cr_single_axle_counter_unfilled", imgPath: "./../../assets/imgs/circuit_images/svgs/single_axle_counter_central_unfilled.svg"},
    ],
    "Track Circuits": [
      {name: "IL Bottom Right", type: CIRCUIT_TYPES.TRACK_CIRCUITS, sub_type: TRACK_CIRCUIT_SUB_TYPES.IL_BOTTOM_RIGHT, id: "il_bottom_right", imgPath: "./../../assets/imgs/circuit_images/svgs/il_bottom_right.svg"},
      {name: "IL Top Left", type: CIRCUIT_TYPES.TRACK_CIRCUITS, sub_type: TRACK_CIRCUIT_SUB_TYPES.IL_TOP_LEFT, id: "il_top_left", imgPath: "./../../assets/imgs/circuit_images/svgs/il_top_left.svg"},
      {name: "IL Top Right", type: CIRCUIT_TYPES.TRACK_CIRCUITS, sub_type: TRACK_CIRCUIT_SUB_TYPES.IL_TOP_RIGHT, id: "il_top_right", imgPath: "./../../assets/imgs/circuit_images/svgs/il_top_right.svg"},
      {name: "IL Bottom Left", type: CIRCUIT_TYPES.TRACK_CIRCUITS, sub_type: TRACK_CIRCUIT_SUB_TYPES.IL_BOTTOM_LEFT, id: "il_bottom_left", imgPath: "./../../assets/imgs/circuit_images/svgs/il_bottom_left.svg"},
      {name: "L Bottom Right", type: CIRCUIT_TYPES.TRACK_CIRCUITS, sub_type: TRACK_CIRCUIT_SUB_TYPES.L_BOTTOM_RIGHT, id: "l_bottom_right", imgPath: "./../../assets/imgs/circuit_images/svgs/l_bottom_right.svg"},
      {name: "L Top Left", type: CIRCUIT_TYPES.TRACK_CIRCUITS, sub_type: TRACK_CIRCUIT_SUB_TYPES.L_TOP_LEFT, id: "l_top_left", imgPath: "./../../assets/imgs/circuit_images/svgs/l_top_left.svg"},
      {name: "L Top Right", type: CIRCUIT_TYPES.TRACK_CIRCUITS, sub_type: TRACK_CIRCUIT_SUB_TYPES.L_TOP_RIGHT, id: "l_top_right", imgPath: "./../../assets/imgs/circuit_images/svgs/l_top_right.svg"},
      {name: "L Bottom Left", type: CIRCUIT_TYPES.TRACK_CIRCUITS, sub_type: TRACK_CIRCUIT_SUB_TYPES.L_BOTTOM_LEFT, id: "l_bottom_left", imgPath: "./../../assets/imgs/circuit_images/svgs/l_bottom_left.svg"},
      {name: "Strict Right", type: CIRCUIT_TYPES.TRACK_CIRCUITS, sub_type: TRACK_CIRCUIT_SUB_TYPES.STRICT_RIGHT, id: "right_bracket", imgPath: "./../../assets/imgs/circuit_images/svgs/right-bracket.svg"},
      {name: "Strict Left", type: CIRCUIT_TYPES.TRACK_CIRCUITS, sub_type: TRACK_CIRCUIT_SUB_TYPES.STRICT_LEFT, id: "left_bracket", imgPath: "./../../assets/imgs/circuit_images/svgs/left-bracket.svg"},
      {name: "Manual Trap Point", type: CIRCUIT_TYPES.TRACK_CIRCUITS, sub_type: TRACK_CIRCUIT_SUB_TYPES.MANUAL_TRAP_POINT, id: "manual_trap_point", imgPath: "./../../assets/imgs/circuit_images/svgs/manual_trap_point_stick_and_bob.svg"},
    ]
  }

export const  legendFilterConfig = {
    general: {
      sipLayout: { label: 'SIP Layout', checked: false },
      tinsLayout: { label: 'Tins Layout', checked: false },
      rfidLayout: { label: 'RFID Tag Layout', checked: true },
    },
    tags: {
      normalTags: { label: 'Normal Tags', checked: false },
      signalFootTags: { label: 'Signal Foot Tags', checked: false },
      turnoutTags: { label: 'Tin Discrimination / Turnout Tags', checked: false },
      exitTags: { label: 'Exit Tags', checked: false },
      gateTags: { label: 'Gate Tags', checked: false },
      adjustmentTags: { label: 'Adjustment / Junction Tags', checked: false },
      lineAdjustmentTags: { label: 'Adjacent Line Tags', checked: false },
      deadStopTags: { label: 'Dead Stop Tags', checked: false },
      adjacentStationTags: { label: 'Adjacent Station Tags', checked: false }
    },

    tins: {
      selectedTrack: null as string | null,
      trackTins: [] as TrackTin[],
      checkedTins: {} as Record<string, boolean>
    }
  };

  export type ZoneName = keyof typeof ZONE_BASED_TRACK_CIRCUIT_CONFIG;
  export const ZONE_BASED_TRACK_CIRCUIT_CONFIG = {
  "Western Railway": {
    height: 26,
    aboveGap: -30,
    belowGap: 26,
  }
} as const;

export const STATION_TABLE_NAMES = {
  author_table: 'author_table',
  revision_history_table: 'revision_table',
  note_table: 'note_table',
  legend_tag_color_table: 'legend_tag_color_table',
  sil0_note_table: 'sil0_note_table',
  station_info_table: 'station_info_table',
  legend_table: 'legend_table',
  approved_table: 'approved_table',
  border_line_tags_table: 'border_line_tags_table',
  right_legend_table: 'right_legend_table',
  ip_port_table: 'ip_port_table',
  division_table: 'division_table',
  head_quarters_table: 'head_quarters_table',
  check_approved_table: 'check_approved_table',
  checked_by_approved_by_table: 'checked_by_approved_by_table',
  signal_telecommunication_single_data_table: 'signal_telecommunication_single_data_table',
  signal_telecommunication_double_data_table: 'signal_telecommunication_double_data_table',
  ref_sig_plan_table: 'ref_sig_plan_table',
  ref_sig_plan_checked_table: 'ref_sig_plan_checked_table',
  signatures_table: 'signatures_table',
  station_signature_table: 'station_signature_table',
  signal_telecommunication_ref_sig_plan_table: 'signal_telecommunication_ref_sig_plan_table',
  fm_table:'fm_table'
};
  export const SIGNAL_SCALE_VALUES = [
        { label: '1x', value: 0 },
        { label: '1.25x', value: 1.25 },
        { label: '1.5x', value: 1.5 },
        { label: '1.75x', value: 1.75 },
        { label: '2x', value: 2 },
        { label: 'Custom', value: -1 },
      ];

  export const SECTION_TYPES = {
    intermidiate:"Intermediate Block Section",
    sil0: 'SIL-0',
  }

export type FontStyle = {
  fontSize: number;
  fontWeight: 'normal' | 'bold';
  color: string;
};

export const DEFAULT_FONT_SIZES: Record<string, any> = {
  rfidTags: {
    tagTypeSize:18,
    fontSize: 16,
    fontWeight: 'bold',
    color: '#000',              // text color
    tagColor: '#0000ff',        // tag fill color (blue)
    borderColor: '#0000ff',     // tag border color (blue)
    strokeWidth: 1,             // border stroke width
    strokeDashArray: ''         // solid border (empty = solid, '7,4' = dashed)
  },
  rfidTagLocation: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  commPoints: {
    strokeColor: 'red',         // communication point border color
    strokeWidth: 2,             // border stroke width
    strokeDashArray: '7,4',     // dashed border pattern
    fillColor: 'none'           // no fill
  },
  tins: { fontSize: 14, fontWeight: 'normal', color: '#000' },
  tableStationTitle: { fontSize: 28, fontWeight: 'bold', color: '#F00' },
  tableContent: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  tableHeaders: { fontSize: 22, fontWeight: 'bold', color: '#000' },
  signals: { fontSize: 18, fontWeight: 'normal', color: '#000' },
  turnouts: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  trapPoints: { fontSize: 16, fontWeight: 'bold', color: '#000' },
  trackNames: { fontSize: 20, fontWeight: 'bold', color: '#000' },
  junctions: { fontSize: 26, fontWeight: 'bold', color: '#000' },
  junctionSub: { fontSize: 26, fontWeight: 'normal', color: '#000' },
  trackSections: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  distancePairText: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  boardText: { fontSize: 14, fontWeight: 'normal', color: '#000' },
  ympPoints: {
    fontSize: 16,
    fontWeight: 'normal',
    color: '#000',              // text color
    arrowColor: 'red',          // arrow and dashed line color
    ellipseColor: 'red'         // ellipse border color
  },
  fmPoints: { fontSize: 18, fontWeight: 'normal', color: '#000' },
  commText: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  centerLineText: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  deadends: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  utilText: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  adjustments: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  trackDirections: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  stationDirections: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  tinNumbers: {
    fontSize: 26,
    fontWeight: 'bold',
    color: '#ff0000',           // text color
      tagColor: '#ff0000',     // tag fill color (pink)
    borderColor: '#ff0000'   // tag border color (pink)
  },
  gateTexts: { fontSize: 26, fontWeight: 'bold', color: '#000' },
  stationTitles: { fontSize: 80, fontWeight: 'normal', color: '#000' }
};

export const DEFAULT_FONT_SIZES_SCR: Record<string, any> = {
  rfidTags: {
     tagTypeSize:18,
    fontSize: 16,
    fontWeight: 'bold',
    color: '#000',              // text color
    tagColor: '#000000',        // tag fill color (black for SCR)
    borderColor: '#000000',     // tag border color (black for SCR)
    strokeWidth: 1,             // border stroke width
    strokeDashArray: ''         // solid border
  },
  rfidTagLocation: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  commPoints: {
    strokeColor: 'red',         // communication point border color
    strokeWidth: 2,             // border stroke width
    strokeDashArray: '7,4',     // dashed border pattern
    fillColor: 'none'           // no fill
  },
  tins: { fontSize: 14, fontWeight: 'normal', color: '#000' },
  tableStationTitle: { fontSize: 28, fontWeight: 'bold', color: '#F00' },
  tableContent: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  tableHeaders: { fontSize: 22, fontWeight: 'bold', color: '#000' },
  signals: { fontSize: 20, fontWeight: 'normal', color: '#000' },
  turnouts: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  trapPoints: { fontSize: 16, fontWeight: 'bold', color: '#000' },
  trackNames: { fontSize: 26, fontWeight: 'bold', color: '#000' },
  junctions: { fontSize: 26, fontWeight: 'bold', color: '#000' },
  junctionSub: { fontSize: 26, fontWeight: 'normal', color: '#000' },
  trackSections: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  distancePairText: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  boardText: { fontSize: 14, fontWeight: 'normal', color: '#000' },
  ympPoints: {
    fontSize: 16,
    fontWeight: 'normal',
    color: '#000',              // text color
    arrowColor: 'black',        // arrow and dashed line color (black for SCR)
    ellipseColor: 'red'         // ellipse border color
  },
  fmPoints: { fontSize: 18, fontWeight: 'normal', color: '#000' },
  commText: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  centerLineText: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  deadends: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  utilText: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  adjustments: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  trackDirections: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  stationDirections: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  tinNumbers: {
    fontSize: 26,
    fontWeight: 'bold',
    color: '#000',           // text color
    tagColor: '#000000',     // tag fill color (black for SCR)
    borderColor: '#000000'   // tag border color (black for SCR)
  },
  gateTexts: { fontSize: 26, fontWeight: 'bold', color: '#000' },
  stationTitles: { fontSize: 80, fontWeight: 'normal', color: '#000' }
};

export const DEFAULT_FONT_SIZES_SCR_HBL: Record<string, any> = {
  rfidTags: {
    tagTypeSize:18,
    fontSize: 16,
    fontWeight: 'bold',
    color: '#000',              // text color
    tagColor: '#0000ff',        // tag fill color (blue)
    borderColor: '#0000ff',     // tag border color (blue for SCR HBL)
    strokeWidth: 1,             // border stroke width
    strokeDashArray: ''         // solid border
  },
  rfidTagLocation: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  commPoints: {
    strokeColor: 'red',         // communication point border color
    strokeWidth: 2,             // border stroke width
    strokeDashArray: '17,10',     // dashed border pattern
    fillColor: 'none'           // no fill
  },
  tins: { fontSize: 14, fontWeight: 'normal', color: '#000' },
  tableStationTitle: { fontSize: 28, fontWeight: 'bold', color: '#F00' },
  tableContent: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  tableHeaders: { fontSize: 22, fontWeight: 'normal', color: '#000' },
  signals: { fontSize: 18, fontWeight: 'normal', color: '#000' },
  turnouts: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  trapPoints: { fontSize: 16, fontWeight: 'bold', color: '#000' },
  trackNames: { fontSize: 26, fontWeight: 'normal', color: '#000' },
  junctions: { fontSize: 26, fontWeight: 'normal', color: '#000' },
  junctionSub: { fontSize: 26, fontWeight: 'normal', color: '#000' },
  trackSections: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  distancePairText: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  boardText: { fontSize: 14, fontWeight: 'normal', color: '#000' },
  ympPoints: {
    fontSize: 16,
    fontWeight: 'normal',
    color: '#000',              // text color
    arrowColor: 'black',        // arrow and dashed line color (black for SCR HBL)
    ellipseColor: 'red'         // ellipse border color
  },
  fmPoints: { fontSize: 18, fontWeight: 'normal', color: '#000' },
  commText: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  centerLineText: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  deadends: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  utilText: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  adjustments: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  trackDirections: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  stationDirections: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  tinNumbers: {
    fontSize: 26,
    fontWeight: 'normal',
    color: '#000',           // text color
    tagColor: '#ff0000',     // tag fill color (red)
    borderColor: '#ff0000'   // tag border color (red for SCR HBL)
  },
  gateTexts: { fontSize: 26, fontWeight: 'normal', color: '#000' },
  stationTitles: { fontSize: 80, fontWeight: 'normal', color: '#000' }
};

export const DEFAULT_FONT_SIZES_NCR: Record<string, any> = {
  rfidTags: {
    tagTypeSize:18,
    fontSize: 16,
    fontWeight: 'bold',
    color: '#000',              // text color
    tagColor: '#0000ff',        // tag fill color (blue)
    borderColor: '#0000ff',     // tag border color (blue for SCR HBL)
    strokeWidth: 1,             // border stroke width
    strokeDashArray: ''         // solid border
  },
  rfidTagLocation: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  commPoints: {
    strokeColor: 'red',         // communication point border color
    strokeWidth: 2,             // border stroke width
    strokeDashArray: '17,10',     // dashed border pattern
    fillColor: 'none'           // no fill
  },
  tins: { fontSize: 14, fontWeight: 'normal', color: '#000' },
  tableStationTitle: { fontSize: 28, fontWeight: 'bold', color: '#F00' },
  tableContent: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  tableHeaders: { fontSize: 22, fontWeight: 'normal', color: '#000' },
  signals: { fontSize: 18, fontWeight: 'normal', color: '#000' },
  turnouts: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  trapPoints: { fontSize: 16, fontWeight: 'bold', color: '#000' },
  trackNames: { fontSize: 26, fontWeight: 'normal', color: '#000' },
  junctions: { fontSize: 26, fontWeight: 'normal', color: '#000' },
  junctionSub: { fontSize: 26, fontWeight: 'normal', color: '#000' },
  trackSections: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  distancePairText: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  boardText: { fontSize: 14, fontWeight: 'normal', color: '#000' },
  ympPoints: {
    fontSize: 16,
    fontWeight: 'normal',
    color: '#000',              // text color
    arrowColor: 'black',        // arrow and dashed line color (black for SCR HBL)
    ellipseColor: 'red'         // ellipse border color
  },
  fmPoints: { fontSize: 18, fontWeight: 'normal', color: '#000' },
  commText: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  centerLineText: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  deadends: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  utilText: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  adjustments: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  trackDirections: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  stationDirections: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  tinNumbers: {
    fontSize: 26,
    fontWeight: 'normal',
    color: '#000',           // text color
    tagColor: '#ff0000',     // tag fill color (red)
    borderColor: '#ff0000'   // tag border color (red for SCR HBL)
  },
  gateTexts: { fontSize: 26, fontWeight: 'normal', color: '#000' },
  stationTitles: { fontSize: 80, fontWeight: 'normal', color: '#000' },
  single_telecommunication: { color : 'black', table_width: 1200 },
};


export const DEFAULT_FONT_SIZES_WR: Record<string, any> = {
  rfidTags: {
     tagTypeSize:18,
    fontSize: 16,
    fontWeight: 'bold',
    color: '#000',              // text color
    tagColor: '#0000ff',        // tag fill color (blue)
    borderColor: '#0000ff',     // tag border color (blue)
    strokeWidth: 1,             // border stroke width
    strokeDashArray: ''         // solid border (empty = solid, '7,4' = dashed)
  },
  rfidTagLocation: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  commPoints: {
    strokeColor: 'red',         // communication point border color
    strokeWidth: 2,             // border stroke width
    strokeDashArray: '7,4',     // dashed border pattern
    fillColor: 'none'           // no fill
  },
  tins: { fontSize: 14, fontWeight: 'normal', color: '#000' },
  tableStationTitle: { fontSize: 28, fontWeight: 'bold', color: '#F00' },
  tableContent: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  tableHeaders: { fontSize: 22, fontWeight: 'bold', color: '#000' },
  signals: { fontSize: 18, fontWeight: 'normal', color: '#000' },
  turnouts: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  trapPoints: { fontSize: 16, fontWeight: 'bold', color: '#000' },
  trackNames: { fontSize: 20, fontWeight: 'bold', color: '#000' },
  junctions: { fontSize: 26, fontWeight: 'bold', color: '#000' },
  junctionSub: { fontSize: 26, fontWeight: 'normal', color: '#000' },
  trackSections: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  distancePairText: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  boardText: { fontSize: 14, fontWeight: 'normal', color: '#000' },
  ympPoints: {
    fontSize: 16,
    fontWeight: 'normal',
    color: '#000',              // text color
    arrowColor: 'red',          // arrow and dashed line color
    ellipseColor: 'red'         // ellipse border color
  },
  fmPoints: { fontSize: 18, fontWeight: 'normal', color: '#000' },
  commText: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  centerLineText: { fontSize: 20, fontWeight: 'normal', color: '#000' },
  deadends: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  utilText: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  adjustments: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  trackDirections: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  stationDirections: { fontSize: 16, fontWeight: 'normal', color: '#000' },
  tinNumbers: {
    fontSize: 26,
    fontWeight: 'bold',
    color: '#fd41ab',           // text color
    tagColor: '#fd41ab',     // tag fill color (red)
    borderColor: '#fd41ab'   // tag border color (red)
  },
  gateTexts: { fontSize: 26, fontWeight: 'bold', color: '#000' },
  stationTitles: { fontSize: 80, fontWeight: 'normal', color: '#000' }
};

export const signalMapping = {
  [SignalType.PERMISSIVE_DISTANT_SIGNAL]: ['Permissive Distant Signal'],
  [SignalType.PERMISSIVE_INNER_DISTANT_SIGNAL]: ['Permissive Inner Distant Signal'],
  [SignalType.GATE_SIGNAL]: ['Gate Cum Distant Signal', 'Gate Cum Inner Distant Signal', 'Gate Cum IB Distant Signal', 'Gate Cum IB Inner Distant Signal'],
  [SignalType.BORDER_SIGNAL]: ['Advance Starter Cum Gate Distant Signal with b symbol', 'Advance Starter Cum IB Distant Signal with b symbol', 'Advance Starter Cum Gate Inner Distant Signal with b symbol', 'Advance Starter Cum Distant Signal with b symbol', 'Advance Starter cum Gate Signal with b symbol'],
  [SignalType.ADVANCE_STARTER_SIGNAL]: ['Advance Starter Cum Gate Distant Signal', 'Advance Starter Cum IB Distant Signal', 'Advance Starter Cum Gate Inner Distant Signal', 'Advance Starter Cum Distant Signal', 'Advance Starter cum Gate Signal'],
  [SignalType.INTERMEDIATE_BLOCK_WITH_BSYMBOL]: ['IB Signal Cum Distant Signal with B symbol', 'IB Signal Cum Inner Distant Signal with B symbol', 'IB Signal Cum Gate Distant Signal with B symbol'],
  [SignalType.INTERMEDIATE_BLOCK_SIGNAL]: ['IB Signal Cum Distant Signal', 'IB Signal Cum Inner Distant Signal', 'IB Signal Cum Gate Distant Signal'],
  [SignalType.AUTOMATIC_SIGNAL]: ['Automatic Advance Starter Signal', 'Automatic Starter Signal'],
  [SignalType.SEMI_AUTOMATIC_SIGNAL]: ['Semi Automatic Advance Starter Signal', 'Semi Automatic Starter Signal']
}

export const SEMI_AUTOMATIC_SERVICE_SIGNALS = ['Semi Automatic Signal','Semi Automatic Starter Signal', 'Semi Automatic Gate Signal', 'Semi Automatic Calling Home Signal', 'Semi Automatic Shunt-Starter Signal', 'Semi Automatic Shunt Calling Home Signal', 'Semi Automatic Gate Calling Home Signal','Calling Home Signal','Shunt-Starter Signal']

export const AllowedSignals = [
  SignalType.CALLING_HOME_SIGNAL,
  SignalType.SHUNT_SIGNAL,
  SignalType.DOUBLE_SHUNT_SIGNAL,
  SignalType.SPI_SHUNT_SIGNAL,
  SignalType.SPI_SHUNT_CALLING_HOME_SIGNAL,
  SignalType.SPI_SIGNAL,
  SignalType.SHUNT_CALLING_HOME_SIGNAL,
  SignalType.SHUNT_STARTER_SIGNAL,
  SignalType.AUTOMATIC_SHUNT_STARTER_SIGNAL,
  SignalType.SEMI_AUTOMATIC_SHUNT_STARTER_SIGNAL,
  SignalType.SEMI_AUTOMATIC_SHUNT_CALLING_HOME_SIGNAL,
  SignalType.SLB_SIGNAL,
  SignalType.SEMI_AUTOMATIC_CALLING_HOME_SIGNAL,
  SignalType.AUTOMATIC_CALLING_HOME_SIGNAL,
  SignalType.AUTOMATIC_GATE_CALLING_HOME_SIGNAL,
  SignalType.SEMI_AUTOMATIC_GATE_CALLING_HOME_SIGNAL,
  SignalType.BSLB_SIGNAL,
  SignalType.SEMI_AUTOMATIC_SIGNAL,
  SignalType.SEMI_AUTOMATIC_STARTER_SIGNAL,
  SignalType.SEMI_AUTOMATIC_GATE_SIGNAL,
  SignalType.AUTOMATIC_GATE_SIGNAL,
  SignalType.APPROACHING_STATION_BOUNDARY_SIGNAL,
  SignalType.BORDER_SIGNAL,
  SignalType.ADVANCE_STARTER_SIGNAL,
  SignalType.AUTOMATIC_STARTER_SIGNAL,
  SignalType.IB_SIGNAL_CUM_DISTANT_SIGNAL,
  SignalType.IB_SIGNAL_CUM_INNER_DISTANT_SIGNAL,
  SignalType.IB_SIGNAL_CUM_GATE_DISTANT_SIGNAL,
  SignalType.IB_SIGNAL_CUM_DISTANT_SIGNAL_WITH_B_SYMBOL,
  SignalType.IB_SIGNAL_CUM_INNER_DISTANT_SIGNAL_WITH_B_SYMBOL,
  SignalType.IB_SIGNAL_CUM_GATE_DISTANT_SIGNAL_WITH_B_SYMBOL,
  SignalType.ADVANCE_STARTER_CUM_GATE_DISTANT_SIGNAL_WITH_B_SYMBOL,
  SignalType.ADVANCE_STARTER_CUM_IB_DISTANT_SIGNAL_WITH_B_SYMBOL,
  SignalType.ADVANCE_STARTER_CUM_GATE_INNER_DISTANT_SIGNAL_WITH_B_SYMBOL,
  SignalType.ADVANCE_STARTER_CUM_DISTANT_SIGNAL_WITH_B_SYMBOL,
  SignalType.ADVANCE_STARTER_CUM_GATE_SIGNAL_WITH_B_SYMBOL,
  SignalType.GATE_SIGNAL,
  SignalType.GATE_CUM_DISTANT_SIGNAL,
  SignalType.GATE_CUM_INNER_DISTANT_SIGNAL,
  SignalType.GATE_CUM_IB_DISTANT_SIGNAL,
  SignalType.GATE_CUM_IB_INNER_DISTANT_SIGNAL,
  SignalType.PERMISSIVE_INNER_DISTANT_SIGNAL,
  SignalType.PERMISSIVE_DISTANT_SIGNAL,
  SignalType.CALLING_HOME_SIGNAL_WITH_B_SYMBOL,
  SignalType.INTERMEDIATE_BLOCK_SIGNAL,
  SignalType.STARTER_SIGNAL,
  SignalType.AUTOMATIC_SIGNAL,
  SignalType.SEMI_AUTOMATIC_ADVANCE_STARTER_SIGNAL,
  SignalType.AUTOMATIC_ADVANCE_STARTER_SIGNAL,
  SignalType.MODIFIED_SEMI_AUTOMATIC_SIGNAL,
  SignalType.SEMI_AUTOMATIC_BORDER_SIGNAL,
  SignalType.INTERMEDIATE_BLOCK_WITH_BSYMBOL,
  SignalType.CALLING_ON_STARTER_SIGNAL
];

/** Style keys of the signal and telecommunication double table. */
export const DOUBLE_TABLE_STYLE_KEY = {
  TOP_LEFT: 'topLeftStyle',
  TITLE: 'titleStyle',
  SYSTEM_NAME: 'systemNameStyle',
  LEFT_PANEL: 'leftPanelStyle',
  STATION_NAME: 'stationNameStyle',
  STATION_INFO: 'stationInfoStyle',
  LABEL: 'labelStyle',
  VALUE: 'valueStyle',
} as const;

export const SHOW_FEATURE = {
  KPS_764 : false,
  ASPECT_CONTROL_CHART: false
}
